/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import React, { ReactElement } from 'react';
import { Button, FlexBox } from '@ui5/webcomponents-react';
import * as storageUtils from '../../utils/storageUtils';
import { setLanguage } from '@ui5/webcomponents-base/dist/config/Language.js';
import { UserLocalStorage, UserSettingRequest, UserSettings } from '../../types';
import { actions } from '../../store/Products';
import { modifyAppContextData } from '../../store/feedbackSlice';
import { AxiosHeaders } from 'axios';
import { Dialog } from '../../controls/Dialog/Dialog';
import { useTranslations } from '../../hooks/useTranslations';
import { removeBrowserSuffix } from '../../utils/utils';
import { loadingActions } from '@/store/loadingSlice';
import { IASLanguageMap, languageMap } from '@/commons';
import axiosInstance from '@/utils/axiosInstance';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { isFallbackLanguage } from '@/utils/langUtils';
import { refreshUserCredentials } from '@/utils/utils';

interface ModalLayoutProps {
    headerText: string;
    children: ReactElement;
    onClose: () => void;
    isShowDialog?: boolean;
}

const ModalLayout: React.FC<ModalLayoutProps> = ({ headerText, children, onClose, isShowDialog = false }) => {
    const userSettings: UserSettings = useAppSelector(state => state?.productList.userSettings);
    const dispatch = useAppDispatch();
    const { t } = useTranslations();
    const navigate = useNavigate();
    const location = useLocation();

    const updateUserSetting = async (userSetting: UserSettingRequest) => {
        const headers = new AxiosHeaders();
        headers.set('Content-Type', 'application/json');
        await axiosInstance.patch(`/ias/user/setting`, JSON.stringify(userSetting), {
            headers,
        });
    };

    const updateUserLocalStorage = () => {
        const userLocalStorage: UserLocalStorage = storageUtils.getItem('user');
        userLocalStorage.locale = userSettings.locale;
        userLocalStorage.time_format = userSettings.timeFormat;
        storageUtils.setItem('user', userLocalStorage);
        dispatch(
            actions.setUserSettings({
                ...userSettings,
                selectedLocal: userSettings.locale,
            })
        );
        dispatch(modifyAppContextData({ languageTag: userSettings.locale }));
    };

    const handleModalSave = async () => {
        onClose();
        const { locale, selectedLocal, timeFormat, selectedTimeFormat, isValid, crmId, selectedCRMId } = userSettings;
        if (
            (locale === selectedLocal &&
                timeFormat === selectedTimeFormat &&
                (crmId === null || selectedCRMId === null || crmId === selectedCRMId)) ||
            !isValid
        ) {
            dispatch(
                actions.setUserSettings({
                    ...userSettings,
                    locale: userSettings.selectedLocal,
                    isValid: true,
                })
            );
            return;
        }
        const newLocale = isFallbackLanguage(locale.toLowerCase()) ? 'fallback' : locale;
        const localeFormatted = languageMap[newLocale?.toLowerCase()] || newLocale?.toLowerCase();
        const iasLanguageFormatted = IASLanguageMap[locale] || locale;

        const userSettingRequest: UserSettingRequest = {
            locale: removeBrowserSuffix(iasLanguageFormatted),
            time_format: timeFormat,
            selectedCRMId,
        };
        dispatch(loadingActions.startLoading());
        try {
            await updateUserSetting(userSettingRequest);
            await setLanguage(localeFormatted);
            updateUserLocalStorage();
        } catch (error) {
            console.error(error);
        } finally {
            const currentPath = location.pathname + location.search;
            navigate(currentPath);
            // only do logout if selected CRM has changed
            if (crmId != selectedCRMId) await refreshUserCredentials();
            dispatch(loadingActions.stopLoading());
        }
    };

    const handleModalClose = () => {
        dispatch(
            actions.setUserSettings({
                ...userSettings,
                locale: userSettings.selectedLocal,
                isValid: true,
            })
        );
        onClose();
    };

    if (!isShowDialog) {
        return null;
    }

    return (
        <Dialog
            open={isShowDialog}
            onBeforeClose={handleModalClose}
            className="ui5-dialog"
            data-ui5-compact-size
            accessibleName={t('SAVE_BUTTON')}
            headerText={headerText}
            footer={
                <FlexBox style={{ gap: 8, width: '100%' }} justifyContent="End" alignItems="Center">
                    <Button id="saveBtn" data-testId="save-btn" design="Emphasized" onClick={handleModalSave} disabled={!userSettings.isValid}>
                        {t('SAVE_BUTTON')}
                    </Button>
                    <Button data-testId="cancel-btn" onClick={() => handleModalClose()}>
                        {t('CANCEL_BUTTON')}
                    </Button>
                </FlexBox>
            }
        >
            {children}
        </Dialog>
    );
};

export default ModalLayout;
